import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/atoms';
import { usePlanGate } from '@/components/organisms';
import {
  PageHeader,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/components/molecules';
import { cn } from '@/lib/utils';
import type { ColumnDef } from '@tanstack/react-table';
import {
  SalesTab,
  TransactionsTab,
  ProductsTab,
  CustomersTab,
  ShareModal,
} from './components';
import { ReceiptSheet } from '../sales/components';
import { TransactionDetailModal } from '../transactions/components/transaction-detail-modal';
import type { TransactionUI } from '../transactions/types';
import type { Sale, Contact } from '@/types';
import {
  useAnalyticsSales,
  useAnalyticsTransactions,
  useAnalyticsProducts,
  useAnalyticsCustomers,
  useProducts,
} from '@/api';
import { fetchTransactions } from '@/api/queries/transactions/request';
import {
  fetchOrders,
  fetchOrder,
  ORDERS_QUERY_KEY,
  ORDER_QUERY_KEY,
} from '@/api/queries/sales';
import {
  getCustomerDisplayName,
  transformOrderToSale,
} from '../sales/utils/orderToSale';
import { transformTransactions } from '../transactions/utils/transformTransaction';
import type { Transaction, Order } from '@/types';
import { Badge, badgeVariants } from '@/components/atoms';
import type { VariantProps } from 'class-variance-authority';

const transactionStatusMap: Record<
  TransactionUI['status'],
  VariantProps<typeof badgeVariants>['variant']
> = {
  success: 'success',
  pending: 'warning',
  failed: 'destructive',
};

// Empty chart data with zero values
const emptyChartData = [
  { month: 'Jan', sales: 0 },
  { month: 'Feb', sales: 0 },
  { month: 'Mar', sales: 0 },
  { month: 'Apr', sales: 0 },
  { month: 'May', sales: 0 },
  { month: 'Jun', sales: 0 },
  { month: 'Jul', sales: 0 },
  { month: 'Aug', sales: 0 },
  { month: 'Sep', sales: 0 },
  { month: 'Oct', sales: 0 },
  { month: 'Nov', sales: 0 },
  { month: 'Dec', sales: 0 },
];

const EMPTY_TRANSACTIONS: Transaction[] = [];

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

// Chart configuration
const chartConfig = {
  sales: {
    label: 'Sales',
    color: '#22c55e',
  },
};

// Mock contact data
const contactData: Contact[] = [
  {
    id: '1',
    name: 'Chris Friedkly',
    initials: 'CF',
    channel: 'whatsapp',
    lastMessage: 'I need a body oil how much?',
    time: '2 min ago',
  },
  {
    id: '2',
    name: 'Maggie Johnson',
    initials: 'MJ',
    channel: 'whatsapp',
    lastMessage: 'I need a Nike shoe size 45.',
    time: '5 min ago',
  },
  {
    id: '3',
    name: 'Gael Harry',
    initials: 'GH',
    channel: 'instagram',
    lastMessage: 'New York Finest Fruits',
    time: '1 hour ago',
  },
  {
    id: '4',
    name: 'Jenna Sullivan',
    initials: 'JS',
    channel: 'facebook',
    lastMessage: 'Walmart',
    time: '2 hours ago',
  },
  {
    id: '5',
    name: 'Alice Smith',
    initials: 'AS',
    channel: 'whatsapp',
    lastMessage: 'Thanks for the quick response!',
    time: '30 min ago',
  },
  {
    id: '6',
    name: 'Bob Wilson',
    initials: 'BW',
    channel: 'instagram',
    lastMessage: 'I will think about it',
    time: '3 hours ago',
  },
];

// Mock sales data for table display (fallback when no analytics data)
const mockSalesData: Sale[] = [
  {
    id: '1',
    name: 'Isaiah Chris',
    products: '2 Shoes',
    amount: '₦8,400',
    status: 'Pending',
    payment: 'Paid',
    date: '10 mins ago',
  },
  {
    id: '2',
    name: 'Mayowa Grace',
    products: '1 Shoes',
    amount: '₦18,400',
    status: 'Pending',
    payment: 'Paid',
    date: '10 mins ago',
  },
  {
    id: '3',
    name: 'Mercy Ada',
    products: '2 Begs',
    amount: '₦8,400',
    status: 'Pending',
    payment: 'Paid',
    date: '10 mins ago',
  },
];

export const AnalyticsPage = () => {
  const { entitlements, allowsFeature, isReady } = usePlanGate();
  const analyticsAllowed = isReady && entitlements.analytics !== false;
  const [activeTab, setActiveTab] = useState('sales');

  useEffect(() => {
    if (!analyticsAllowed) allowsFeature('analytics');
  }, [allowsFeature, analyticsAllowed]);

  // Fetch analytics data
  const { data: salesData } = useAnalyticsSales();
  const { data: transactionsData } = useAnalyticsTransactions();
  const { data: productsData } = useAnalyticsProducts();
  const { data: customersData } = useAnalyticsCustomers();

  const { data: productsList = [] } = useProducts();
  const { data: txResponse } = useQuery({
    queryKey: ['transactions', { status: undefined, search: '' }],
    queryFn: () => fetchTransactions({}),
  });
  const { data: ordersResponse } = useQuery({
    queryKey: [ORDERS_QUERY_KEY, { pageSize: 20 }],
    queryFn: () => fetchOrders({ pageSize: 20 }),
  });

  const orders = useMemo(
    () => (ordersResponse?.data ?? []) as Order[],
    [ordersResponse?.data]
  );

  const transactions = useMemo(
    () => (txResponse?.data ?? EMPTY_TRANSACTIONS) as Transaction[],
    [txResponse?.data]
  );

  const parseAmount = useCallback((amount?: string) => {
    if (!amount) return 0;
    return parseFloat(String(amount).replace(/[^\d.-]/g, '')) || 0;
  }, []);

  const isSuccess = useCallback(
    (t: Transaction) =>
      String(t.status ?? '')
        .toLowerCase()
        .includes('success'),
    []
  );

  const groupByMonthSum = useCallback(
    (items: Transaction[], predicate?: (t: Transaction) => boolean) => {
      const sums = new Array(12).fill(0);
      items.forEach((t) => {
        if (predicate && !predicate(t)) return;
        const d = t.createdAt ? new Date(t.createdAt) : null;
        if (!d || isNaN(d.getTime())) return;
        sums[d.getMonth()] += parseAmount(t.amount);
      });
      return MONTH_LABELS.map((m, idx) => ({ month: m, sales: sums[idx] }));
    },
    [parseAmount]
  );

  const fallbackSalesChart = useMemo(
    () => groupByMonthSum(transactions, isSuccess),
    [groupByMonthSum, isSuccess, transactions]
  );
  const fallbackTransactionsChart = useMemo(
    () => groupByMonthSum(transactions),
    [groupByMonthSum, transactions]
  );
  const fallbackSalesTotal = useMemo(
    () =>
      transactions
        .filter(isSuccess)
        .reduce((s, t) => s + parseAmount(t.amount), 0),
    [isSuccess, parseAmount, transactions]
  );
  const fallbackPayoutTotal = useMemo(
    () => transactions.reduce((s, t) => s + parseAmount(t.amount), 0),
    [parseAmount, transactions]
  );

  const totalProducts = productsList.length;
  const totalStockQuantity = productsList.reduce(
    (s, p) => s + (p.quantity ?? 0),
    0
  );

  const recentOrdersForTables = useMemo(() => {
    const sorted = [...orders].sort((a, b) => {
      const da = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const db = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return db - da;
    });
    return sorted.slice(0, 10);
  }, [orders]);

  const recentSalesFromOrders = useMemo(() => {
    return recentOrdersForTables.map((order) => transformOrderToSale(order));
  }, [recentOrdersForTables]);

  const topSalesBuyer = useMemo(() => {
    const paidOrders = orders.filter(
      (o) => o.status === 'paid' || o.status === 'completed'
    );
    let best:
      | { order: Order; productName: string; lineTotal: number }
      | undefined;

    for (const order of paidOrders) {
      for (const item of order.orderItems ?? []) {
        const lineTotal = (item?.productPriceAt ?? 0) * (item?.quantity ?? 0);
        if (!best || lineTotal > best.lineTotal) {
          best = {
            order,
            productName:
              item?.product?.name ??
              (item?.productId != null
                ? `Product ${item.productId}`
                : 'Product'),
            lineTotal,
          };
        }
      }
    }

    if (!best) return { name: '—', subtitle: '—' };

    const c = best.order.customer;
    const contactBits = [c?.email, c?.phoneNumber].filter(Boolean).join(' • ');
    const subtitle = contactBits
      ? `${contactBits} • ${best.productName}`
      : `Bought ${best.productName}`;

    return { name: getCustomerDisplayName(best.order), subtitle };
  }, [orders]);

  const topTransactionBuyer = useMemo(() => {
    // Prefer successful transactions; fallback to any if status strings differ.
    const candidates = transactions.filter(isSuccess);
    const pickFrom = candidates.length ? candidates : transactions;
    if (!pickFrom.length) return { name: '—', subtitle: '—' };

    let best = pickFrom[0]!;
    for (const t of pickFrom) {
      if (parseAmount(t.amount) > parseAmount(best.amount)) best = t;
    }

    const orderId = best.orderId;
    const order =
      orderId != null
        ? orders.find((o) => o.id?.toString() === orderId.toString())
        : undefined;

    if (!order) {
      return {
        name: orderId != null ? `Customer ${orderId}` : '—',
        subtitle: best.reference ? `Ref: ${best.reference}` : '—',
      };
    }

    const c = order.customer;
    const contactBits = [c?.email, c?.phoneNumber].filter(Boolean).join(' • ');

    // Try to add a product hint from the order
    const bestItem = (order.orderItems ?? []).reduce(
      (acc, cur) => {
        const lineTotal = (cur?.productPriceAt ?? 0) * (cur?.quantity ?? 0);
        if (!acc || lineTotal > acc.lineTotal) {
          return {
            productName:
              cur?.product?.name ??
              (cur?.productId != null ? `Product ${cur.productId}` : 'Product'),
            lineTotal,
          };
        }
        return acc;
      },
      undefined as undefined | { productName: string; lineTotal: number }
    );

    const productHint = bestItem?.productName;
    const subtitleBase =
      contactBits || (best.reference ? `Ref: ${best.reference}` : '—');
    const subtitle = productHint
      ? `${subtitleBase} • ${productHint}`
      : subtitleBase;

    return { name: getCustomerDisplayName(order), subtitle };
  }, [isSuccess, orders, parseAmount, transactions]);

  const mostViewedProduct = useMemo(() => {
    const counts = new Map<
      string,
      { name: string; qty: number; hits: number }
    >();

    for (const order of recentOrdersForTables) {
      for (const item of order.orderItems ?? []) {
        const key =
          item?.productId != null
            ? `id:${item.productId}`
            : item?.product?.name
              ? `name:${item.product.name}`
              : 'unknown';
        const name =
          item?.product?.name ??
          (item?.productId != null
            ? `Product ${item.productId}`
            : 'Unknown product');
        const qty = item?.quantity ?? 0;
        const prev = counts.get(key);
        counts.set(key, {
          name,
          qty: (prev?.qty ?? 0) + qty,
          hits: (prev?.hits ?? 0) + 1,
        });
      }
    }

    let best: { name: string; qty: number; hits: number } | undefined;
    for (const v of counts.values()) {
      if (!best) best = v;
      else if (v.qty > best.qty) best = v;
      else if (v.qty === best.qty && v.hits > best.hits) best = v;
    }

    if (!best) return { name: '—', countLabel: '—' };
    return {
      name: best.name,
      countLabel: `${best.qty} ordered`,
    };
  }, [recentOrdersForTables]);

  const effectiveSalesData = {
    totalSales: salesData?.totalSales ?? fallbackSalesTotal,
    dateRange: salesData?.dateRange ?? {},
    chart: salesData?.chart?.length ? salesData.chart : fallbackSalesChart,
  };

  const effectiveTransactionsData = useMemo(
    () => ({
      totalPayouts:
        transactions.length > 0
          ? fallbackPayoutTotal
          : (transactionsData?.totalPayouts ?? 0),
      dateRange: transactionsData?.dateRange ?? {},
      chart:
        transactions.length > 0
          ? fallbackTransactionsChart
          : transactionsData?.chart?.length
            ? transactionsData.chart
            : [],
    }),
    [
      transactions.length,
      fallbackPayoutTotal,
      fallbackTransactionsChart,
      transactionsData?.totalPayouts,
      transactionsData?.dateRange,
      transactionsData?.chart,
    ]
  );

  const { topMonth: salesTopMonth, topYear: salesTopYear } = useMemo(() => {
    const chart = effectiveSalesData.chart;
    if (!chart?.length)
      return { topMonth: '—', topYear: new Date().getFullYear().toString() };
    const best = chart.reduce((acc, cur) =>
      cur.sales > acc.sales ? cur : acc
    );
    return {
      topMonth: best.month,
      topYear: new Date().getFullYear().toString(),
    };
  }, [effectiveSalesData.chart]);

  const { topMonth: txTopMonth, topYear: txTopYear } = useMemo(() => {
    const chart = effectiveTransactionsData.chart;
    if (!chart?.length)
      return { topMonth: '—', topYear: new Date().getFullYear().toString() };
    const best = chart.reduce((acc, cur) =>
      cur.sales > acc.sales ? cur : acc
    );
    return {
      topMonth: best.month,
      topYear: new Date().getFullYear().toString(),
    };
  }, [effectiveTransactionsData.chart]);

  const salesCountLabel = effectiveSalesData.chart.length
    ? `${effectiveSalesData.totalSales?.toLocaleString() ?? 0} sold so far`
    : '0 sold so far';
  const transformedTransactions = useMemo(
    () => transformTransactions(transactions),
    [transactions]
  );
  const recentTransactionsUI = useMemo(
    () =>
      [...transformedTransactions]
        .sort((a, b) => {
          const da = new Date(a.fullDate).getTime();
          const db = new Date(b.fullDate).getTime();
          return db - da;
        })
        .slice(0, 10),
    [transformedTransactions]
  );

  // Modal state management
  const [selectedSale, setSelectedSale] = useState<Sale | null>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState<
    string | null
  >(null);
  const [showTransactionDetail, setShowTransactionDetail] = useState(false);

  const { data: selectedOrder } = useQuery({
    queryKey: [ORDER_QUERY_KEY, selectedOrderId],
    queryFn: () => fetchOrder(selectedOrderId!),
    enabled: !!selectedOrderId,
  });

  const handleRowClick = (sale: Sale) => {
    setSelectedSale(sale);
    setSelectedOrderId(sale.id);
    setShowReceipt(true);
  };

  const handleTransactionsRowClick = useCallback((tx: TransactionUI) => {
    setSelectedTransactionId(tx.id);
    setShowTransactionDetail(true);
  }, []);

  const transactionColumns: ColumnDef<TransactionUI>[] = useMemo(
    () => [
      { accessorKey: 'refId', header: 'Ref ID' },
      { accessorKey: 'orderId', header: 'Order ID' },
      {
        accessorKey: 'amount',
        header: 'Amount',
        cell: ({ row }) => (
          <span className="font-semibold">{row.original.amount}</span>
        ),
      },
      { accessorKey: 'method', header: 'Method' },
      { accessorKey: 'gateway', header: 'Gateway' },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge variant={transactionStatusMap[row.original.status]}>
            {row.original.statusLabel}
          </Badge>
        ),
      },
      { accessorKey: 'date', header: 'Date' },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <Button
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={() => handleTransactionsRowClick(row.original)}
          >
            View
          </Button>
        ),
      },
    ],
    [handleTransactionsRowClick]
  );

  const txCountLabel = effectiveTransactionsData.chart.length
    ? `${transformedTransactions.length} transactions so far`
    : '0 transactions so far';

  const handleCloseReceipt = () => {
    setShowReceipt(false);
    setSelectedSale(null);
    setSelectedOrderId(null);
  };

  const handleShareReceipt = () => {
    setShowShareModal(true);
  };

  const handleCloseShareModal = () => {
    setShowShareModal(false);
  };

  const handleContactSelect = (contact: Contact) => {
    // Simulate sharing receipt to contact
    alert(`Receipt shared to ${contact.name} via ${contact.channel}!`);
    setShowShareModal(false);
  };

  // Define columns for Recent Sales table
  const columns: ColumnDef<Sale>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <div className="font-medium">{row.getValue('name')}</div>
      ),
    },
    {
      accessorKey: 'products',
      header: 'Products',
      cell: ({ row }) => <div>{row.getValue('products')}</div>,
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      cell: ({ row }) => (
        <div className="font-medium">{row.getValue('amount')}</div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row.getValue('status') as string;
        return <span className="text-sm">{status}</span>;
      },
    },
    {
      accessorKey: 'payment',
      header: 'Payment',
      cell: ({ row }) => {
        const payment = row.getValue('payment') as string;
        return (
          <div className="flex items-center gap-2">
            <div
              className={cn(
                'w-2 h-2 rounded-full',
                payment === 'Paid' ? 'bg-green-500' : 'bg-gray-400'
              )}
            />
            <span
              className={cn(
                payment === 'Paid' ? 'text-green-500' : 'text-gray-500'
              )}
            >
              {payment}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: 'date',
      header: 'Date',
      cell: ({ row }) => (
        <div className="text-sm text-gray-500">{row.getValue('date')}</div>
      ),
    },
    {
      id: 'actions',
      header: 'View',
      cell: ({ row }) => (
        <Button
          variant="outline"
          size="sm"
          className="text-xs"
          onClick={() => handleRowClick(row.original)}
        >
          View
        </Button>
      ),
    },
  ];

  if (!isReady) return null;

  if (!analyticsAllowed) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader
          heading="Analytics"
          description="View your store analytics and performance"
        />
        <Button className="w-fit" onClick={() => allowsFeature('analytics')}>
          Upgrade to view analytics
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        heading="Analytics"
        description="View your store analytics and performance"
      />

      {/* Analytics Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="sales">Sales</TabsTrigger>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
          <TabsTrigger value="products">Products</TabsTrigger>
          {/* <TabsTrigger value="customers">Customers</TabsTrigger> */}
        </TabsList>

        <TabsContent value="sales">
          <SalesTab
            showEmptyChart={effectiveSalesData.chart.length === 0}
            showAnalyticsEmptyState={recentSalesFromOrders.length === 0}
            onRowClick={handleRowClick}
            salesData={recentSalesFromOrders}
            columns={columns}
            monthlySalesData={effectiveSalesData.chart}
            emptyChartData={emptyChartData}
            chartConfig={chartConfig}
            analyticsData={effectiveSalesData}
            topMonth={salesTopMonth}
            topYear={salesTopYear}
            topBuyer={topSalesBuyer.name}
            topBuyerSubtitle={topSalesBuyer.subtitle}
            salesCountLabel={salesCountLabel}
          />
        </TabsContent>

        <TabsContent value="transactions">
          <TransactionsTab
            showEmptyChart={effectiveTransactionsData.chart.length === 0}
            showAnalyticsEmptyState={recentTransactionsUI.length === 0}
            onRowClick={handleTransactionsRowClick}
            transactionData={recentTransactionsUI}
            transactionColumns={transactionColumns}
            monthlySalesData={effectiveTransactionsData.chart}
            emptyChartData={emptyChartData}
            chartConfig={chartConfig}
            analyticsData={effectiveTransactionsData}
            topMonth={txTopMonth}
            topYear={txTopYear}
            topBuyer={topTransactionBuyer.name}
            topBuyerSubtitle={topTransactionBuyer.subtitle}
            txCountLabel={txCountLabel}
          />
        </TabsContent>

        <TabsContent value="products">
          <ProductsTab
            showEmptyChart={(productsData?.chart?.length ?? 0) === 0}
            showAnalyticsEmptyState={productsList.length === 0}
            onRowClick={handleRowClick}
            salesData={recentSalesFromOrders}
            columns={columns}
            monthlySalesData={productsData?.chart ?? []}
            emptyChartData={emptyChartData}
            chartConfig={chartConfig}
            analyticsData={productsData}
            totalProducts={totalProducts}
            totalStockQuantity={totalStockQuantity}
            mostViewedProductName={mostViewedProduct.name}
            mostViewedProductCountLabel={mostViewedProduct.countLabel}
          />
        </TabsContent>

        <TabsContent value="customers">
          <CustomersTab
            showEmptyChart={(customersData?.barchart?.length ?? 0) === 0}
            showAnalyticsEmptyState={
              (customersData?.barchart?.length ?? 0) === 0
            }
            onRowClick={handleRowClick}
            salesData={mockSalesData}
            columns={columns}
            monthlySalesData={
              customersData?.barchart?.map((item) => ({
                month: item.month,
                sales: item.customers,
              })) || []
            }
            emptyChartData={emptyChartData}
            chartConfig={chartConfig}
            analyticsData={customersData}
          />
        </TabsContent>
      </Tabs>

      {/* Receipt Sheet (uses sales ReceiptSheet for Download PDF + real data) */}
      <ReceiptSheet
        showReceipt={showReceipt}
        setShowReceipt={setShowReceipt}
        selectedSale={selectedSale}
        selectedOrder={selectedOrder ?? null}
        onClose={handleCloseReceipt}
        onShare={handleShareReceipt}
      />

      {/* Share Receipt Modal */}
      <ShareModal
        showShareModal={showShareModal}
        setShowShareModal={setShowShareModal}
        onClose={handleCloseShareModal}
        onContactSelect={handleContactSelect}
        contacts={contactData}
      />

      {/* Transaction detail (by id) for Analytics Transactions tab */}
      <TransactionDetailModal
        open={showTransactionDetail}
        onOpenChange={setShowTransactionDetail}
        onClose={() => {
          setShowTransactionDetail(false);
          setSelectedTransactionId(null);
        }}
        transaction={
          selectedTransactionId
            ? ({
                id: selectedTransactionId,
                refId: '',
                amount: '',
                status: 'pending',
                statusLabel: 'Loading...',
                paymentMethod: '',
                accountNumber: '',
                transactionType: '',
                method: '',
                gateway: '',
                date: '',
                fullDate: '',
              } satisfies TransactionUI)
            : null
        }
      />
    </div>
  );
};
